import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { EyeIcon, PaperAirplaneIcon, CheckIcon, XMarkIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { formatDate, getStatusClass, formatCurrency } from '../../utils';
import { apiRequest } from '../../utils/apiHelper';
import { toast } from '../../utils/notification';
import DataTable from '../../components/common/DataTable';

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiRequest('/api/billing/invoices/');
      setInvoices(data?.results || data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
      setError('Failed to load invoices. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      await apiRequest(`/api/billing/invoices/${id}/`, 'PATCH', { status: newStatus });
      toast.success(`Invoice status updated to ${newStatus}`);
      fetchInvoices();
    } catch (err) {
      console.error('Error updating invoice status:', err);
      toast.error('Failed to update invoice status.');
    }
  };

  const columns = [
    {
      key: 'invoice_number',
      label: 'Invoice Number',
      render: (row) => (
        <Link to={`/billing/invoices/${row.id}`} className="text-primary-600 dark:text-primary-400 hover:underline">
          {row.invoice_number}
        </Link>
      ),
    },
    {
      key: 'client',
      label: 'Client',
      className: 'text-gray-900 dark:text-white',
      render: (row) =>
        row.client_detail?.full_name || `${row.client_detail?.first_name || ''} ${row.client_detail?.last_name || ''}`.trim() || 'Unknown Client',
      sortValue: (row) =>
        row.client_detail?.full_name || `${row.client_detail?.first_name || ''} ${row.client_detail?.last_name || ''}`.trim(),
    },
    {
      key: 'issue_date',
      label: 'Issue Date',
      render: (row) => formatDate(row.issue_date),
      sortValue: (row) => (row.issue_date ? new Date(row.issue_date).getTime() : 0),
    },
    {
      key: 'due_date',
      label: 'Due Date',
      render: (row) => formatDate(row.due_date),
      sortValue: (row) => (row.due_date ? new Date(row.due_date).getTime() : 0),
    },
    {
      key: 'total_amount',
      label: 'Amount',
      headerClassName: 'text-right',
      className: 'text-right text-gray-500 dark:text-gray-300',
      render: (row) => formatCurrency(row.total_amount),
      sortValue: (row) => parseFloat(row.total_amount) || 0,
    },
    {
      key: 'status',
      label: 'Status',
      headerClassName: 'text-center',
      className: 'text-center',
      render: (row) => (
        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClass(row.status)}`}>
          {row.status_display}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      headerClassName: 'text-right',
      className: 'text-right',
      render: (row) => (
        <div className="flex justify-end space-x-2">
          <Link
            to={`/billing/invoices/${row.id}`}
            className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300"
            title="View"
          >
            <EyeIcon className="h-5 w-5" />
          </Link>
          
          {row.status === 'draft' && (
            <button
              onClick={() => handleStatusChange(row.id, 'sent')}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
              title="Mark as Sent"
            >
              <PaperAirplaneIcon className="h-5 w-5" />
            </button>
          )}
          
          {(row.status === 'sent' || row.status === 'overdue') && (
            <button
              onClick={() => handleStatusChange(row.id, 'paid')}
              className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300"
              title="Mark as Paid"
            >
              <CheckIcon className="h-5 w-5" />
            </button>
          )}
          
          {row.status !== 'void' && row.status !== 'paid' && (
            <button
              onClick={() => handleStatusChange(row.id, 'void')}
              className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
              title="Void Invoice"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      ),
    },
  ];

  if (loading && invoices.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
      <div className="py-8">
        <div className="flex justify-between mb-6">
          <h2 className="text-2xl font-semibold leading-tight text-gray-900 dark:text-white">Invoices</h2>
          <Link
            to="/billing/invoices/new"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
          >
            New Invoice
          </Link>
        </div>
        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-4 mb-6">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}
        <DataTable
          columns={columns}
          data={invoices}
          searchPlaceholder="Search invoices…"
          searchKeys={['invoice_number', 'status_display']}
          emptyMessage="No invoices available."
          emptyIcon={DocumentTextIcon}
          defaultSort={{ key: 'issue_date', direction: 'desc' }}
        />
      </div>
    </div>
  );
};

export default Invoices;